import { Grid, Stack, Typography, Alert } from "@mui/material";

import { useEffect, useMemo } from "react";
import { useForm, useWatch } from "react-hook-form";
import { FormSelectInput } from "../../../components/form";

import { usePractitionerRoleContext } from "../../../contexts/PractitionerRoleContext";
import { useSessionContext } from "../../../contexts/SessionContext";
import { useValueSet } from "../../../hooks/useValueSet";
import { AvailableDays } from "./AvailableDays";
import { AvailableTime } from "./AvailableTime";
import { RequiredField } from "./RequiredField";
import { SubmitActions } from "./SubmitActions";
import { useFormatter } from "@/utils/useFormatter";
import { currentFeatures } from "@/configs/Settings";
import { FormAutoComplete } from "@/components/form";

function cleanRepeatedList(list: any) {
  let cleanedList = new Set(list);
  return Array.from(cleanedList);
}

/** Profissional com pelo menos uma especialidade com display configurado. */
function hasConfiguredSpecialty(
  resource?: fhir5.PractitionerRole | null,
): boolean {
  return Boolean(
    resource?.specialty?.some(
      (e: fhir5.CodeableConcept) => e.coding?.[0]?.display,
    ),
  );
}

export function HealthCare({
  onNext,
  setCurrentStep,
  defaultValues,
}: any) {
  const { access } = useSessionContext();
  const { practitionerRole, userPractitionerRole } =
    usePractitionerRoleContext();

  const isManager = Boolean(access?.roles?.includes("manager"));

  // useForm fica antes do cálculo de serviços porque, para o gestor,
  // os serviços dependem do profissional selecionado no formulário.
  const { control, handleSubmit, register, unregister, setValue } = useForm({
    defaultValues,
  });

  const selectedProfessionalActor = useWatch({
    name: "professionalActor",
    control,
  });

  // Gestor: o profissional é escolhido PRIMEIRO.
  // Aqui buscamos o PractitionerRole dele para extrair as especialidades (serviços).
  const selectedPractitionerRole = selectedProfessionalActor?.value
    ? practitionerRole?.find(
        (el: fhir5.BundleEntry<fhir5.PractitionerRole>) =>
          el.resource?.practitioner?.reference ===
          selectedProfessionalActor.value,
      )?.resource
    : undefined;

  // Só ativos COM especialidade alimentam o select de serviço
  // (inativos / sem especialidade não devem oferecer serviço de saúde).
  // Gestor: apenas o profissional selecionado (sem seleção = nenhum serviço ainda).
  const availablePractitioner = isManager
    ? selectedPractitionerRole?.active !== false &&
      hasConfiguredSpecialty(selectedPractitionerRole)
      ? [selectedPractitionerRole]
      : []
    : userPractitionerRole?.active !== false &&
        hasConfiguredSpecialty(userPractitionerRole)
      ? [userPractitionerRole]
      : [];

  const availableSpecialites =
    availablePractitioner
      ?.map((el: any) => {
        const specialty = el?.specialty
          ?.map((e: fhir5.CodeableConcept) => e.coding?.[0]?.display)
          ?.filter(Boolean);
        return specialty;
      })
      .filter((array: any) => array !== undefined && array.length > 0) || [];

  const cleanAvailableSpecialites = cleanRepeatedList(
    [].concat.apply([], availableSpecialites),
  );

  // Hook para buscar especialidades médicas usando useValueSet
  const { options: specialtiesOptions, loading: specialtiesLoading } =
    useValueSet({
      valueSet: "CBO", // ajuste conforme seu ValueSet

      customUrl: "https://fluxmed.cloud/fhir/r4/ValueSet/CBO-S", // ajuste se necessário
    });

  const daysofweek = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sab"];
  const defaultDays = ["Seg", "Ter", "Qua", "Qui", "Sex"];

  let watchDaysChosen = useWatch({ name: "daysOfWeek", control });
  const selectedHealthService = useWatch({ name: "healthService", control });

  const selectedDays = watchDaysChosen || defaultDays;

  function removeUnselectedDays(object: any, daysToRemove: any) {
    for (const day of daysToRemove) {
      if (object?.hasOwnProperty(day)) {
        delete object[day];
      }
    }
  }

  const onSubmit = async (data: any) => {
    const unselectedDays = daysofweek.filter(
      (el: any) => !selectedDays.includes(el),
    );

    removeUnselectedDays(data?.timeAvailable?.[0], unselectedDays);
    removeUnselectedDays(data?.timeAvailable?.[1], unselectedDays);

    await onNext({ healthcare: data });
  };

  const clickNext = async (data: any) => {
    await onSubmit(data);
    setCurrentStep((prev: number) => prev + 1);
  };

  // Processar especialidades baseado nas opções do ValueSet e especialidades disponíveis
  const specialties = (() => {
    if (specialtiesLoading) {
      return [{ value: "", display: "Carregando" }];
    }

    const auxList =
      specialtiesOptions
        ?.filter((option) => option?.display?.includes("Médico "))
        ?.map((item) => ({
          value: item.display,
          display:
            useFormatter.formatNameDisplay(
              item.display?.split("Médico ")?.[1],
            ) || item.display,
        }))
        ?.filter((el: any) => cleanAvailableSpecialites.includes(el.display)) ||
      [];

    const otherSpecialties =
      cleanAvailableSpecialites
        ?.filter((e: any) => !auxList?.some((el: any) => el?.display === e))
        ?.map((item: any) => ({
          display: item,
          value: item,
        })) || [];

    return [...auxList, ...otherSpecialties];
  })();

  // Gestor sem profissional escolhido: lista de serviços vazia é esperada, não é erro.
  const isWaitingProfessional = isManager && !selectedProfessionalActor?.value;

  const hasNoSpecialtiesForService =
    !specialtiesLoading && !isWaitingProfessional && specialties.length === 0;

  const practitionerOptions = useMemo(() => {
    return (
      practitionerRole
        ?.filter((element: fhir5.BundleEntry<fhir5.PractitionerRole>) => {
          // Ativo, com nome e especialidade configurada.
          // Não filtra mais pelo serviço: o profissional é escolhido antes do serviço.
          const hasDisplay = !!element?.resource?.practitioner?.display;
          const isActive = element?.resource?.active !== false;
          const hasSpecialty = hasConfiguredSpecialty(element?.resource);

          return hasDisplay && isActive && hasSpecialty;
        })
        ?.map((el: fhir5.BundleEntry<fhir5.PractitionerRole>) => {
          return {
            label: useFormatter.formatNameDisplay(
              el.resource?.practitioner?.display || "",
            ),
            value: el.resource?.practitioner?.reference || "",
          };
        }) || []
    );
  }, [practitionerRole]);

  // Gestor sem nenhum profissional elegível: mantém o aviso de "nenhum serviço disponível".
  const hasNoPractitionerAvailable =
    isManager && Boolean(practitionerRole) && practitionerOptions.length === 0;

  // Limpa o profissional se ele não for mais elegível (ex.: ficou inativo).
  useEffect(() => {
    if (!isManager) return;
    if (!practitionerRole) return;
    if (!selectedProfessionalActor?.value) return;

    const isSelectedProfessionalAvailable = practitionerOptions.some(
      (option) => option.value === selectedProfessionalActor.value,
    );

    if (isSelectedProfessionalAvailable) return;

    setValue("professionalActor", null, {
      shouldDirty: true,
      shouldValidate: true,
    });
  }, [
    isManager,
    practitionerRole,
    practitionerOptions,
    selectedProfessionalActor,
    setValue,
  ]);

  // Gestor: o serviço depende do profissional.
  // Ao trocar de profissional, ajusta o serviço para um que ele realmente oferece.
  // Só roda com practitionerRole carregado, para não apagar valores salvos (voltar/editar).
  useEffect(() => {
    if (!isManager) return;
    if (!practitionerRole) return;
    if (specialtiesLoading) return;

    const isSelectedServiceAvailable = specialties.some(
      (option) => option.value === selectedHealthService,
    );
    if (isSelectedServiceAvailable) return;

    // fluxvet: o serviço vem do perfil, então já preenche com a 1ª especialidade.
    // fluxmed: só limpa e deixa o gestor escolher.
    const nextHealthService = currentFeatures.defaultApp
      ? ""
      : specialties[0]?.value || "";

    if ((selectedHealthService || "") === nextHealthService) return;

    setValue("healthService", nextHealthService, {
      shouldDirty: true,
      shouldValidate: Boolean(nextHealthService),
    });
  }, [
    isManager,
    practitionerRole,
    specialtiesLoading,
    specialties,
    selectedHealthService,
    setValue,
  ]);

  return (
    <Stack mb={2} width="100%" spacing={2}>
      <Typography
        variant="h6"
        fontWeight="600"
        textAlign="center"
        padding="16px 0px 16px 0px"
        color="neutral700.main"
      >
        Configure o tipo e horário do serviço de saúde associado à esta agenda.
      </Typography>

      {/* Gestor: o profissional vem ANTES do serviço, pois os serviços dependem dele. */}
      {isManager && (
        <Grid
          container
          justifyContent="center"
          alignItems="center"
          paddingBottom={2}
        >
          <Grid item xs={12}>
            <Typography variant="h6" fontWeight="600" color="neutral600.main">
              Profissional associado a essa agenda
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={1}>
              Profissionais ativos com especialidade configurada.
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <FormAutoComplete
              name="professionalActor"
              label="Digite o nome do profissional"
              control={control}
              selectOptions={practitionerOptions}
              autoFocus
            />
          </Grid>
        </Grid>
      )}

      <Grid
        container
        justifyContent="center"
        alignItems="center"
        paddingBottom={2}
      >
        <Grid item xs={12}>
          <Typography
            variant="h6"
            fontWeight="600"
            mb={1}
            color="neutral600.main"
          >
            Serviço de saúde <RequiredField />
          </Typography>

          <Typography variant="body2" color="text.secondary" mb={2}>
            {isManager
              ? isWaitingProfessional
                ? "Selecione primeiro o profissional para ver os serviços que ele oferece."
                : "Especialidades configuradas no perfil do profissional selecionado."
              : "Só é possível criar agenda se o seu perfil estiver ativo e com especialidade configurada."}
          </Typography>

          {!currentFeatures.defaultApp && (
            <Typography variant="body1" mb={2}>
              O serviço de saúde da agenda está associado a especialidade
              configurada no perfil profissional.
            </Typography>
          )}

          {(hasNoSpecialtiesForService || hasNoPractitionerAvailable) && (
            <Alert severity="info" sx={{ mb: 2 }}>
              {isManager
                ? "Nenhum serviço disponível. Cadastre ou edite a especialidade dos profissionais ativos em Configurações → Profissionais / perfil profissional."
                : "Nenhum serviço disponível. Configure sua especialidade no perfil profissional e tente novamente."}
            </Alert>
          )}
        </Grid>

        <Grid item xs={12}>
          <FormSelectInput
            defaultValue={
              currentFeatures.defaultApp ? "" : specialties[0]?.value
            }
            name="healthService"
            label="Serviço de saúde"
            control={control}
            selectOptions={specialties}
            required
            autoFocus={!isManager}
            readOnly={hasNoSpecialtiesForService || isWaitingProfessional}
          />
        </Grid>
      </Grid>
      <Grid container justifyContent="center" alignItems="center">
        <Grid item xs={12}>
          <Typography
            variant="h6"
            fontWeight="600"
            mb={2}
            color="neutral600.main"
          >
            Dias e horários disponíveis na semana <RequiredField />
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <Typography variant="body1" mb={2}>
            Configure os horários em que este serviço ficará disponível durante
            a semana.
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <AvailableDays
            selectedDays={selectedDays}
            control={control}
            register={register}
          />
        </Grid>
        <Grid item xs={12}>
          <AvailableTime
            selectedDays={selectedDays}
            control={control}
            defaultValues={defaultValues}
            unregister={unregister}
          />
        </Grid>
        <Grid item xs={12} mt={5}>
          <SubmitActions
            goBack={() => setCurrentStep((prev: number) => prev - 1)}
            goForward={handleSubmit(clickNext)}
          />
        </Grid>
      </Grid>
    </Stack>
  );
}
